import { NoddiIcon } from 'noddi-ui-common';
import { useState, type ReactNode } from 'react';
import { DateRange } from 'react-day-picker';
import { Button } from '../../../../../base/button/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../../../../base/dropdown-menu';
import type { DateRangeControl, FilterControl } from '../../../../../types/filterControl';
import { NoddiBadge } from '../../../../Other/NoddiBadge';
import { NoddiDateRangePicker } from '../../../../Overlay/Date/NoddiDateRangePicker';
import { hasDateRangeValue } from './helpers';

export const DateRangeFilter = ({
  control,
  renderFilterTitle
}: {
  control: DateRangeControl;
  renderFilterTitle: (control: FilterControl) => ReactNode;
}) => {
  const { value, onChange } = control;
  const hasInclude = hasDateRangeValue(value.include);
  const hasExclude = hasDateRangeValue(value.exclude);

  const [mode, setMode] = useState<'include' | 'exclude'>(hasExclude && !hasInclude ? 'exclude' : 'include');
  // When switching mode we clear the other side, which briefly leaves both empty.
  // Skip one "both empty → include" reset so Exclude can open with an empty picker.
  const [skipEmptyReset, setSkipEmptyReset] = useState(false);
  const [syncedPresence, setSyncedPresence] = useState({ hasInclude, hasExclude });

  // Sync mode from external value changes (URL hydrate, chip remove, clear all).
  // Adjust during render rather than in an effect to avoid cascading re-renders.
  if (syncedPresence.hasInclude !== hasInclude || syncedPresence.hasExclude !== hasExclude) {
    setSyncedPresence({ hasInclude, hasExclude });

    if (!hasInclude && !hasExclude) {
      if (skipEmptyReset) {
        setSkipEmptyReset(false);
      } else if (mode !== 'include') {
        setMode('include');
      }
    } else {
      if (skipEmptyReset) {
        setSkipEmptyReset(false);
      }
      if (hasExclude && !hasInclude && mode !== 'exclude') {
        setMode('exclude');
      } else if (hasInclude && !hasExclude && mode !== 'include') {
        setMode('include');
      }
    }
  }

  const picker = (range: DateRange | undefined, onRangeChange: (dateRange: DateRange | undefined) => void) => (
    <NoddiDateRangePicker
      dateRange={range}
      onDateChange={onRangeChange}
      showClearButton
      className='min-w-min'
      smallVersion
    />
  );

  const changeInclude = (include: DateRange | undefined) => onChange({ include, exclude: value.exclude });
  const changeExclude = (exclude: DateRange | undefined) => onChange({ include: value.include, exclude });

  if (!control.canExclude) {
    return (
      <div className='flex flex-col gap-2'>
        {renderFilterTitle(control)}
        {picker(value.include, changeInclude)}
      </div>
    );
  }

  const isExcludeMode = mode === 'exclude';

  const switchToInclude = () => {
    if (hasExclude) {
      setSkipEmptyReset(true);
      changeExclude(undefined);
    }
    setMode('include');
  };

  const switchToExclude = () => {
    if (hasInclude) {
      setSkipEmptyReset(true);
      changeInclude(undefined);
    }
    setMode('exclude');
  };

  const clear = () => {
    setSkipEmptyReset(false);
    onChange({ include: undefined, exclude: undefined });
    setMode('include');
  };

  return (
    <div className='flex flex-col gap-2'>
      {renderFilterTitle(control)}
      <div className='group flex items-start gap-1'>
        <div className='flex flex-1 flex-col gap-1'>
          {isExcludeMode && (
            <NoddiBadge variant='destructive' className='self-start px-1 py-0 text-[10px]'>
              Excluded
            </NoddiBadge>
          )}
          {isExcludeMode ? picker(value.exclude, changeExclude) : picker(value.include, changeInclude)}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='ghost'
              size='sm'
              className='size-6 shrink-0 p-0 opacity-60 group-hover:opacity-100'
              onClick={(e) => e.stopPropagation()}
            >
              <NoddiIcon name='EllipsisVertical' size='small' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuItem onClick={switchToInclude}>
              <NoddiIcon name='Check' size='small' className='text-primary' />
              Include
            </DropdownMenuItem>
            <DropdownMenuItem onClick={switchToExclude}>
              <NoddiIcon name='Minus' size='small' className='text-destructive' />
              Exclude
            </DropdownMenuItem>
            {(hasInclude || hasExclude) && (
              <DropdownMenuItem onClick={clear}>
                <NoddiIcon name='X' size='small' />
                Clear
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
